/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The gym's clock: day/week/month windows and times of day in the gym's time
 * zone (Box.settings.timezone), not the viewer's laptop. DST-correct through
 * Intl, no time-zone library. An unknown or missing zone falls back to the
 * browser's own, so a malformed setting never blanks the page.
 */
import { isUsableTimeZone, wallClockParts, zoneMidnight } from "@lemonadetraining/contracts";
import type { ResolvedTimeFormat } from "./timeFormat";

/** The zone's UTC offset (ms, east-positive) at an instant. Throws for an unknown zone. */
export const tzOffsetMs = (instant: Date, timeZone: string): number => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUTC - (instant.getTime() - instant.getMilliseconds());
};

/** A zone we can use: the gym's when valid, otherwise the browser's. */
export const isKnownZone = (timeZone: string | null | undefined): boolean =>
  isUsableTimeZone(timeZone);

export const usableZone = (timeZone: string | null | undefined): string =>
  isUsableTimeZone(timeZone) ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;

const unknownZone = (timeZone: string) =>
  new RangeError(`Invalid time zone specified: ${timeZone}`);

/** The gym-local calendar date (y, m 1-12, d, weekday 0=Sunday) at an instant. */
export const gymDateParts = (instant: Date, timeZone: string) => {
  const wall = wallClockParts(instant, timeZone);
  if (!wall) throw unknownZone(timeZone);
  const weekday = new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay();
  return { year: wall.year, month: wall.month, day: wall.day, weekday };
};

/** The instant at which the gym's wall clock reads 00:00 on y-m-d (out-of-range m/d roll over). */
export const gymMidnight = (year: number, month: number, day: number, timeZone: string): Date => {
  const key = new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
  const ms = zoneMidnight(key, timeZone);
  if (ms === null) throw unknownZone(timeZone);
  return new Date(ms);
};

export interface GymWindow {
  start: Date;
  /** Exclusive. */
  end: Date;
}

/** The gym's calendar day containing `now`, shifted by `offsetDays`. */
export const gymDayWindow = (now: Date, timeZone: string, offsetDays = 0): GymWindow => {
  const { year, month, day } = gymDateParts(now, timeZone);
  const start = gymMidnight(year, month, day + offsetDays, timeZone);
  const end = gymMidnight(year, month, day + offsetDays + 1, timeZone);
  return { start, end };
};

/** The gym's Monday-to-Sunday week containing `now`. */
export const gymWeekWindow = (now: Date, timeZone: string): GymWindow => {
  const { year, month, day, weekday } = gymDateParts(now, timeZone);
  const sinceMonday = (weekday + 6) % 7;
  return {
    start: gymMidnight(year, month, day - sinceMonday, timeZone),
    end: gymMidnight(year, month, day - sinceMonday + 7, timeZone),
  };
};

/** The gym's calendar month containing `now`, `monthsBack` months earlier. */
export const gymMonthWindow = (now: Date, timeZone: string, monthsBack = 0): GymWindow => {
  const { year, month } = gymDateParts(now, timeZone);
  return {
    start: gymMidnight(year, month - monthsBack, 1, timeZone),
    end: gymMidnight(year, month - monthsBack + 1, 1, timeZone),
  };
};

/** A time of day on the gym's clock, in the viewer's chosen 12/24-hour format. */
export const formatGymTime = (
  instant: Date,
  timeZone: string,
  resolved: ResolvedTimeFormat
): string =>
  new Intl.DateTimeFormat(resolved === "24h" ? "en-GB" : "en-US", {
    timeZone,
    hour: resolved === "24h" ? "2-digit" : "numeric",
    minute: "2-digit",
    hourCycle: resolved === "24h" ? "h23" : "h12",
  }).format(instant);

/** "Saturday 26 September" on the gym's calendar. */
export const formatGymDate = (instant: Date, timeZone: string): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(instant);

/** Short month label for charts ("Sep"), on the gym's calendar. */
export const formatGymMonth = (instant: Date, timeZone: string): string =>
  new Intl.DateTimeFormat("en-GB", { timeZone, month: "short" }).format(instant);

/** "Good morning" / "Good afternoon" / "Good evening" by the gym's clock. */
export const gymGreeting = (instant: Date, timeZone: string): string => {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hourCycle: "h23" }).format(
      instant
    )
  );
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};
